import { useCallback, useEffect, useRef, useState } from 'react';
import { useEscapeToClose, useFocusTrap } from '@/lib/use-escape';
import { humanError } from '@/lib/api-error';

export interface GifSearchResult {
  id: string;
  title: string;
  previewUrl: string;
  mp4Url: string;
  width: number;
  height: number;
}

export type GifSearch = (query: string) => Promise<GifSearchResult[]>;

/** GIF/meme picker (KLIPY). Search → grid → click inserts an overlay. */
export function GifSearchModal({
  search,
  onSelect,
  onClose,
}: {
  search: GifSearch;
  onSelect: (gif: GifSearchResult) => void;
  onClose: () => void;
}) {
  useEscapeToClose(onClose);
  const trapRef = useFocusTrap<HTMLDivElement>();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GifSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);

  const run = useCallback(
    async (q: string) => {
      const term = q.trim();
      if (!term) {
        setResults([]);
        setError(null);
        return;
      }
      const my = ++seq.current;
      setLoading(true);
      setError(null);
      try {
        const hits = await search(term);
        if (my === seq.current) setResults(hits);
      } catch (e) {
        // 서버 원문(KLIPY 오류 등)은 영어다 — 한국어일 때만 그대로 쓴다.
        if (my === seq.current)
          setError(humanError(e, 'GIF 를 불러오지 못했어요. 잠시 뒤 다시 시도해 주세요.'));
      } finally {
        if (my === seq.current) setLoading(false);
      }
    },
    [search]
  );

  // Debounce the query.
  useEffect(() => {
    const t = setTimeout(() => void run(query), 350);
    return () => clearTimeout(t);
  }, [query, run]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      ref={trapRef}
      onClick={onClose}
    >
      <div
        className="flex max-h-[80vh] w-full max-w-[520px] flex-col rounded-lg border bg-card p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="mb-3 text-sm font-semibold">GIF 추가</h2>
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="mb-3 w-full rounded-md border bg-background px-2 py-1.5 text-sm"
          placeholder="검색 (예: shocked, laughing, wow)…"
        />
        {error && <p className="mb-2 text-xs text-destructive">{error}</p>}
        <div className="min-h-[120px] flex-1 overflow-y-auto">
          {loading && results.length === 0 ? (
            <p className="py-12 text-center text-xs text-muted-foreground">검색 중…</p>
          ) : results.length === 0 ? (
            <p className="py-12 text-center text-xs text-muted-foreground">
              {query.trim() ? '결과 없음' : '검색어를 입력하세요.'}
            </p>
          ) : (
            // Masonry (CSS columns) so each GIF shows at its natural aspect ratio.
            <div className="columns-3 gap-2">
              {results.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => onSelect(g)}
                  className="mb-2 block w-full break-inside-avoid overflow-hidden rounded-md border bg-black hover:ring-2 hover:ring-primary"
                  title={g.title}
                >
                  <img
                    src={g.previewUrl}
                    alt={g.title}
                    loading="lazy"
                    className="block h-auto w-full"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="mt-4 flex items-center justify-between">
          <span className="text-[10px] text-muted-foreground">Powered by KLIPY</span>
          <button type="button" onClick={onClose} className="rounded-md border px-3 py-1.5 text-sm">
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}
